import React, { useCallback, memo, useEffect } from "react";
import { useGetFileCarouselHintPortalElement } from "../useCarousel";
import { createPortal } from "react-dom";
import { InferenceProgressHint } from "@/components/flow/build/Hints/InferenceProgressHint";
import { DownloadingHint } from "@/components/flow/build/Hints/DownloadingHint";
import { LoadingOverlay } from "./Loading";
import { BuildSteps, Flows } from "@/types/flows";
import { useFlow } from "@/contexts/FlowContext";
import { AnnotateHint } from "@/components/flow/build/Hints/AnnotateHint";
import { useFlowState } from "@/hooks/useFlowState";
import { useCurrentMedia } from "@/store";
import { FileInputDimensions } from "@/types/media";
import { useIsGeneratingPredictions } from "@/hooks/useIsGeneratingPredictions";

interface ImageFilePreviewProps {
    forwardedRef?: React.Ref<HTMLImageElement>;
    fileUrl: string;
    file: File;
    onMediaReady?: () => void;
    effectiveZoomLevel: number;
    effectiveZoomX: number;
    effectiveZoomY: number;
    // Shared state props
    localNaturalDimensions: FileInputDimensions;
    setLocalNaturalDimensions: (dimensions: FileInputDimensions) => void;
    localRenderedDimensions: FileInputDimensions;
    setLocalRenderedDimensions: (dimensions: FileInputDimensions) => void;
    // States that parent needs to know about
    serverFrameDimensions: { width: number; height: number } | null;
}

const ImageFilePreview = memo<ImageFilePreviewProps>(
    ({
        forwardedRef,
        fileUrl,
        file,
        onMediaReady,
        effectiveZoomLevel,
        effectiveZoomX,
        effectiveZoomY,
        localNaturalDimensions,
        localRenderedDimensions,
        setLocalNaturalDimensions,
        setLocalRenderedDimensions
    }) => {
        const fileCarouselHintPortalElement = useGetFileCarouselHintPortalElement();
        const { flowLocked } = useFlow();
        const { step, flow } = useFlowState();
        const currentMedia = useCurrentMedia();

        const handleImageRef = useCallback(
            (instance: HTMLImageElement | null) => {
                if (typeof forwardedRef === "function") {
                    forwardedRef(instance);
                } else if (forwardedRef) {
                    (forwardedRef as React.MutableRefObject<HTMLImageElement | null>).current =
                        instance;
                }
                if (instance) {
                    onMediaReady?.();
                    requestAnimationFrame(() => {
                        if (instance) {
                            const newDims = {
                                width: instance.clientWidth,
                                height: instance.clientHeight
                            };
                            if (
                                localRenderedDimensions?.width !== newDims.width ||
                                localRenderedDimensions?.height !== newDims.height
                            ) {
                                setLocalRenderedDimensions(newDims);
                            }
                        }
                    });
                }
            },
            [forwardedRef, onMediaReady, localRenderedDimensions, setLocalRenderedDimensions]
        );

        // ensure we update rendered dimensions on window resize
        const handleWindowResize = useCallback(() => {
            if (forwardedRef && typeof forwardedRef === "object" && forwardedRef.current) {
                const instance = forwardedRef.current;
                requestAnimationFrame(() => {
                    if (instance) {
                        const newDims = {
                            width: instance.clientWidth,
                            height: instance.clientHeight
                        };
                        if (
                            localRenderedDimensions?.width !== newDims.width ||
                            localRenderedDimensions?.height !== newDims.height
                        ) {
                            setLocalRenderedDimensions(newDims);
                        }
                    }
                });
            }
        }, [forwardedRef, localRenderedDimensions, setLocalRenderedDimensions]);

        useEffect(() => {
            window.addEventListener("resize", handleWindowResize);
            return () => window.removeEventListener("resize", handleWindowResize);
        }, [handleWindowResize]);

        const handleImageLoad = useCallback(
            async (e: React.SyntheticEvent<HTMLImageElement>) => {
                const img = e.currentTarget;
                const newDims = { width: img.naturalWidth, height: img.naturalHeight };
                if (
                    localNaturalDimensions?.width !== newDims.width ||
                    localNaturalDimensions?.height !== newDims.height
                ) {
                    setLocalNaturalDimensions(newDims);
                }
            },
            [localNaturalDimensions, setLocalNaturalDimensions]
        );

        const handleImageError = useCallback(
            (e: React.SyntheticEvent<HTMLImageElement>) => {
                console.error(`Error loading image from blob URL ${fileUrl}`, e);
            },
            [fileUrl]
        );

        // Check inference status for LoadingOverlay
        const isInferenceInProgress = useIsGeneratingPredictions(currentMedia?.file.id || "");

        return (
            <>
                <img
                    key={fileUrl}
                    onError={handleImageError}
                    ref={handleImageRef}
                    src={fileUrl}
                    alt={file?.name || "Uploaded file preview"}
                    className="absolute left-1/2 top-1/2 max-h-full w-auto max-w-full"
                    style={{
                        transform: `translate(-50%, -50%) scale(${effectiveZoomLevel}) translate(${effectiveZoomX}px, ${effectiveZoomY}px)`,
                        transformOrigin: "center center",
                        pointerEvents: flowLocked ? "none" : "auto"
                    }}
                    onLoad={handleImageLoad}
                />
                {fileCarouselHintPortalElement &&
                    createPortal(
                        <>
                            {step === BuildSteps.PROMPT && <AnnotateHint />}
                            {step === BuildSteps.REVIEW && <DownloadingHint isVideo={false} />}
                            {(step === BuildSteps.REVIEW ||
                                flow === Flows.DEPLOY ||
                                flow === Flows.SHARE) &&
                                currentMedia?.file && (
                                    <InferenceProgressHint fileId={currentMedia.file.id} />
                                )}
                        </>,
                        fileCarouselHintPortalElement
                    )}
            </>
        );
    }
);

ImageFilePreview.displayName = "ImageFilePreview";

export default ImageFilePreview;
